import { useState } from 'react';
import { Plus, Calendar, FileText, Download, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ProblemStatements = () => {
  const { isDark } = useTheme();
  const [problemStatements] = useState([
    {
      id: 'PS-001',
      title: 'Sustainable Energy Solutions for Rural Communities',
      week: 'Week 1, Nov 2025',
      status: 'Published',
      submissions: 12,
      researchPack: true,
      publishedAt: '01/11/2025',
    },
    {
      id: 'PS-002',
      title: 'AI-Powered Healthcare Accessibility',
      week: 'Week 2, Nov 2025',
      status: 'Published',
      submissions: 8,
      researchPack: true,
      publishedAt: '08/11/2025',
    },
    {
      id: 'PS-003',
      title: 'Digital Literacy for Elderly',
      week: 'Week 3, Nov 2025',
      status: 'Draft',
      submissions: 0,
      researchPack: false,
      publishedAt: '-',
    },
    {
      id: 'PS-004',
      title: 'Waste Management Innovation',
      week: 'Week 4, Nov 2025',
      status: 'Scheduled',
      submissions: 0,
      researchPack: true,
      publishedAt: '22/11/2025',
    },
  ]);

  const handlePublish = () => {
    // TODO: Implement publish functionality
    console.log('Publishing new PS...');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Problem Statements</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage weekly problem statements and research packs</p>
        </div>
        <Button 
          onClick={handlePublish} 
          className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New PS
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total PS</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{problemStatements.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Published</p>
          <p className="text-2xl font-bold text-green-600">
            {problemStatements.filter(ps => ps.status === 'Published').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Submissions</p>
          <p className="text-2xl font-bold text-blue-600">
            {problemStatements.reduce((sum, ps) => sum + ps.submissions, 0)}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>With Research Packs</p>
          <p className="text-2xl font-bold text-purple-600">
            {problemStatements.filter(ps => ps.researchPack).length}
          </p>
        </div>
      </div>

      {/* Problem Statements - Desktop Table / Mobile Cards */}
      <div className={`rounded-xl shadow-md border overflow-hidden transition-colors ${
        isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
      }`}>
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>ID</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>TITLE</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>WEEK</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>STATUS</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>SUBMISSIONS</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>RESEARCH PACK</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>PUBLISHED AT</TableHead>
                <TableHead className={`font-semibold transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>ACTIONS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {problemStatements.map((ps) => (
                <tr
                  key={ps.id}
                  className={`border-b transition-colors group ${
                    isDark 
                      ? 'border-purple-500/20 hover:bg-purple-500/10' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <TableCell className={`font-medium transition-colors ${
                    isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                  }`}>{ps.id}</TableCell>
                  <TableCell className={`font-medium transition-colors ${
                    isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                  }`}>{ps.title}</TableCell>
                  <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.week}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ps.status === 'Published'
                          ? isDark
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-green-100 text-green-700'
                          : ps.status === 'Scheduled'
                          ? isDark
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                          : isDark
                            ? 'bg-gray-500/30 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {ps.status}
                    </span>
                  </TableCell>
                  <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.submissions}</TableCell>
                  <TableCell>
                    {ps.researchPack ? (
                      <span className={isDark ? 'text-green-400' : 'text-green-600'}>✓ Available</span>
                    ) : (
                      <span className={isDark ? 'text-gray-400' : 'text-gray-400'}>Not available</span>
                    )}
                  </TableCell>
                  <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{ps.publishedAt}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                        isDark ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' : 'text-red-600'
                      }`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {ps.researchPack && (
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </tr>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-purple-500/20">
          {problemStatements.map((ps) => (
            <div
              key={ps.id}
              className={`p-4 transition-colors ${
                isDark 
                  ? 'hover:bg-purple-500/10' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold transition-colors ${
                      isDark ? 'text-purple-300' : 'text-purple-600'
                    }`}>{ps.id}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        ps.status === 'Published'
                          ? isDark
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-green-100 text-green-700'
                          : ps.status === 'Scheduled'
                          ? isDark
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                          : isDark
                            ? 'bg-gray-500/30 text-gray-300'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {ps.status}
                    </span>
                  </div>
                  <h3 className={`font-semibold mb-2 transition-colors ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{ps.title}</h3>
                </div>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                  }`}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                  }`}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                    isDark ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' : 'text-red-600'
                  }`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Week</p>
                  <p className={`font-medium transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{ps.week}</p>
                </div>
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Submissions</p>
                  <p className={`font-medium transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{ps.submissions}</p>
                </div>
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Research Pack</p>
                  <p className={`font-medium transition-colors ${
                    ps.researchPack 
                      ? isDark ? 'text-green-400' : 'text-green-600'
                      : isDark ? 'text-gray-400' : 'text-gray-400'
                  }`}>
                    {ps.researchPack ? '✓ Available' : 'Not available'}
                  </p>
                </div>
                <div>
                  <p className={`text-xs mb-1 transition-colors ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Published At</p>
                  <p className={`font-medium transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>{ps.publishedAt}</p>
                </div>
              </div>
              {ps.researchPack && (
                <div className="mt-3 pt-3 border-t border-purple-500/20">
                  <Button variant="ghost" size="sm" className={`w-full justify-start ${
                    isDark 
                      ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white' 
                      : 'text-purple-700 hover:bg-purple-50'
                  }`}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Research Pack
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProblemStatements;

